'use client'

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'

import { Dialog, Icon, IconComponent } from '@graftcode/design-system'

import { GRAFTCODE_ONBOARDING_TEST_IDS } from '../../graftcode-onboarding-test-ids.const'
import { getAgentPromptPreviewCode, isAgentPromptPreviewTruncated } from '../../lib/getAgentPromptPreviewCode'

import styles from './GraftcodeOnboardingDashboard.module.scss'

type GraftcodeOnboardingAgentPromptProps = {
  code: string
  label: string
}

export function GraftcodeOnboardingAgentPrompt({ code, label }: GraftcodeOnboardingAgentPromptProps) {
  const t = useTranslations('graftcodeOnboarding')
  const [modalOpen, setModalOpen] = useState(false)
  const modalRootRef = useRef<HTMLDivElement>(null)

  const previewCode = useMemo(() => getAgentPromptPreviewCode(code), [code])
  const truncated = useMemo(() => isAgentPromptPreviewTruncated(code, previewCode), [code, previewCode])

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code)
    } catch (error) {
      console.error('Failed to copy agent prompt:', error)
    }
  }, [code])

  const modalTitleId = 'graftcode-onboarding-agent-prompt-title'

  useLayoutEffect(() => {
    if (!modalOpen) return

    const dialogContent = modalRootRef.current?.closest('.dialog__content') as HTMLElement | null
    if (!dialogContent) return

    dialogContent.style.width = 'min(800px, 90vw)'
    dialogContent.style.maxWidth = 'min(800px, 90vw)'
    dialogContent.style.height = 'min(896px, 90vh)'
    dialogContent.style.maxHeight = 'min(896px, 90vh)'
    dialogContent.style.padding = '0'
    dialogContent.style.alignItems = 'stretch'
    dialogContent.style.overflow = 'hidden'

    const dialogBody = dialogContent.querySelector('.dialog__body') as HTMLElement | null
    if (dialogBody) {
      dialogBody.style.display = 'flex'
      dialogBody.style.flex = '1'
      dialogBody.style.flexDirection = 'column'
      dialogBody.style.alignItems = 'stretch'
      dialogBody.style.gap = '0'
      dialogBody.style.minHeight = '0'
      dialogBody.style.maxHeight = '100%'
      dialogBody.style.padding = '0'
      dialogBody.style.overflow = 'hidden'
    }
  }, [modalOpen])

  return (
    <>
      <article
        className={styles.agentPromptCard}
        aria-label={label}
        data-testid={GRAFTCODE_ONBOARDING_TEST_IDS.agentPrompt}
      >
        <div className={styles.agentPromptCardInner}>
          <header className={styles.agentPromptCardHeader}>
            <div className={styles.agentPromptCardLabel}>
              <IconComponent icon={Icon.StarRegular} size={20} className={styles.agentPromptCardIcon} />
              <span>{label}</span>
            </div>
            <button type="button" className={styles.agentPromptCopyButton} onClick={() => void handleCopy()}>
              <IconComponent icon={Icon.CopyRegular} size={16} aria-hidden />
              {t('dashboard.copyAgentPrompt')}
            </button>
          </header>

          <div className={styles.agentPromptCardBody}>
            <pre className={styles.agentPromptCardCode}>{previewCode}</pre>
            {truncated ? (
              <div className={styles.agentPromptCardFadeArea}>
                <div className={styles.agentPromptCardFade} aria-hidden />
                <button type="button" className={styles.agentPromptExpand} onClick={() => setModalOpen(true)}>
                  <IconComponent icon={Icon.OpenRegular} size={20} aria-hidden />
                  {t('dashboard.expandAgentPrompt')}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </article>

      <Dialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        noPadding
        className={styles.agentPromptDialog}
        ariaLabelledBy={modalTitleId}
        showDismiss
      >
        <div ref={modalRootRef} className={styles.agentPromptModalRoot}>
          <header className={styles.agentPromptModalHeader}>
            <h3 id={modalTitleId} className={styles.agentPromptModalTitle}>
              {label}
            </h3>
            <button type="button" className={styles.agentPromptCopyButton} onClick={() => void handleCopy()}>
              <IconComponent icon={Icon.CopyRegular} size={16} aria-hidden />
              {t('dashboard.copyAgentPrompt')}
            </button>
          </header>
          <div className={styles.agentPromptModalBody}>
            <pre className={styles.agentPromptModalCode}>{code}</pre>
          </div>
        </div>
      </Dialog>
    </>
  )
}
